import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { rootClsPrefix as clsPrefix } from '../constants';
import TitleBar from './TitleBar';

/**
 * @typedef {Object} WindowProps
 * @property {Function} setVisible - Function to hide the popup
 * @property {Object} popupPosition - Ref to store the position of the popup
 * @property {Object} dragging - Ref to store if the popup is being dragged
 * @property {Object} dragPosition - Ref to store the position of the popup and mouse when dragging started
 * @property {string} titlePadding - Padding of the title bar
 * @property {Function} defaultPopupPosition - Function to calculate the default position of the popup
 * @property {Function} onHide - Function to call when the popup is hidden
 * @property {string} title - Title of the popup
 * @property {string} titleDescription - Description of the title
 * @property {string} iconSize - Size of the icon
 * @property {string} popupWidth - Width of the popup
 * @property {React.ReactNode} children - Children of the popup
 * @property {Function} titleBarIcon - Icon of the title bar
 * */

/**
 * @param {WindowProps} props
 * */
const Window = ({
  setVisible,
  popupPosition,
  dragging,
  dragPosition,
  titlePadding,
  defaultPopupPosition,
  onHide = () => {},
  title,
  titleDescription,
  iconSize,
  popupWidth = '40rem',
  children,
  titleBarIcon,
  popupHeight,
  popupPlacement,
}) => {
  const popupRef = useRef();
  const titleBarLastClick = useRef(0);
  const [finalPopupPosition, setFinalPopupPosition] = useState(null);

  const getDefaultPopupPosition = () => {
    const rect = popupRef.current.getBoundingClientRect();
    return defaultPopupPosition(popupWidth, `${rect.height}px`);
  };

  useEffect(() => {
    if (!popupRef.current) return;
    if (!popupPosition.current) {
      setFinalPopupPosition(getDefaultPopupPosition());
    } else {
      setFinalPopupPosition({
        left: popupPosition.current.x,
        top: popupPosition.current.y,
      });
    }
  }, []);

  const hide = () => {
    setVisible(false);
    onHide();
  };

  /**
   * dragPosition ref has the following structure:
   * {
   *    mouse: {
   *      x: x coordinate of mouse when dragging started,
   *      y: y coordinate of mouse when dragging started,
   *    },
   *    popup: {
   *      x: x coordinate of popup when dragging started
   *      y: y coordinate of popup when dragging started
   *    }
   * }
   *
   * DELTA = CURRENT_MOUSE_POSITION - MOUSE_POSITION_WHEN_DRAGGING_STARTED
   * This function adds DELTA vector to the popup position (when dragging started) to get
   * the new position
   *
   * If popupPosition ref is null, the popup will position itself at the bottom right of box.
   */

  const handleMouseMove = e => {
    if (dragging.current) {
      if (!popupPosition.current) popupPosition.current = {};
      const newX =
        dragPosition.current.popup.x + e.clientX - dragPosition.current.mouse.x;
      const newY =
        dragPosition.current.popup.y + e.clientY - dragPosition.current.mouse.y;
      popupPosition.current.x = `${newX}px`;
      popupPosition.current.y = `${newY}px`;
      popupRef.current.style.left = popupPosition.current.x;
      popupRef.current.style.top = popupPosition.current.y;
    }
  };

  const handleMouseUp = () => {
    dragging.current = false;
  };

  const handleOutsideClick = e => {
    if (!popupRef.current) return;
    if (!popupRef.current.contains(e.target)) {
      const balloon = document.querySelector('.balloon');
      if (balloon && balloon.contains(e.target)) return;
      hide();
    }
  };

  const titleBarMouseDown = e => {
    dragging.current = true;
    dragPosition.current.popup = {
      x: popupRef.current.offsetLeft,
      y: popupRef.current.offsetTop,
    };
    dragPosition.current.mouse = { x: e.clientX, y: e.clientY };
  };

  const titleBarOnClick = () => {
    if (Date.now() - titleBarLastClick.current < 300) {
      const defaultPosition = getDefaultPopupPosition();
      popupRef.current.style.left = null;
      popupRef.current.style.top = null;
      popupRef.current.style.right = null;
      popupRef.current.style.bottom = null;
      popupRef.current.style.left = defaultPosition.left;
      popupRef.current.style.top = defaultPosition.top;
      popupRef.current.style.right = defaultPosition.right;
      popupRef.current.style.bottom = defaultPosition.bottom;
      popupPosition.current = null;
    }
    titleBarLastClick.current = Date.now();
  };

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick, true);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('click', handleOutsideClick, true);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);
  return (
    <div
      className={`${clsPrefix}-popup`}
      ref={popupRef}
      data-testid="popup"
      style={{
        position: 'fixed',
        display: 'flex',
        flexDirection: 'column',
        left: finalPopupPosition?.left,
        right: finalPopupPosition?.right,
        top: finalPopupPosition?.top,
        bottom: finalPopupPosition?.bottom,
        width: popupWidth,
        height: popupHeight,
        visibility: finalPopupPosition ? 'visible' : 'hidden',
      }}
    >
      <TitleBar
        onMouseDown={titleBarMouseDown}
        onClick={titleBarOnClick}
        titlePadding={titlePadding}
        hide={hide}
        title={title}
        titleDescription={titleDescription}
        Icon={titleBarIcon}
        iconSize={iconSize}
      />
      <div style={{ flexGrow: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
};

/**
 * @typedef {Object} PopupProps
 * @property {boolean} visible - Whether the popup is visible
 *
 * @param {PopupProps | WindowProps} props
 * */
const Popup = ({ visible, ...rest }) => {
  const popupPosition = useRef();
  const dragging = useRef(false);
  const dragPosition = useRef({});
  if (!visible) return <></>;
  return createPortal(
    <Window
      popupPosition={popupPosition}
      dragging={dragging}
      dragPosition={dragPosition}
      {...rest}
    />,
    document.body,
  );
};
export default Popup;
